import React from 'react';
import { IBlockData } from '@core/typings';
import { BasicType } from '@core/constants';
import { createBlock } from '@core/utils/createBlock';
import { get, merge } from 'lodash';
import { BasicBlock } from '@core/components/BasicBlock';
import {
	formatDynamicTextValue,
	getAncestorPinnedListing,
	resolveBoundListing,
	parseDynamicTextHelperExpression,
	resolveMergeTagValue,
	resolvePreviewColor,
	t,
} from '@core/utils';
import {
	AttachedListingData,
	getPreviewResolutionDataSource,
	resolveAttachedListingMergeTags,
	resolveViewerProfile,
} from '../../../utils/resolveAttachedListingMergeTags';

const MERGE_TAG_PATTERN =
	/(\{\{\{\{raw\}\}\}\})?\{\{\s*([^{}]+?)\s*\}\}(\{\{\{\{\/raw\}\}\}\})?/gi;
const IF_MERGE_TAG_PATTERN =
	/\{\{#if\s+([\w.-]+)\}\}\s*\{\{([^{}]+?)\}\}\s*\{\{else\}\}([\s\S]*?)\{\{\/if\}\}/g;

function normalizeDynamicTextMaxLength(value: unknown): number | null {
	if (value === null || value === undefined || value === '') return null;

	const parsed = Number(value);
	if (!Number.isFinite(parsed)) return null;

	const normalized = Math.floor(parsed);
	return normalized > 0 ? normalized : null;
}

function truncateDynamicTextValue(value: unknown, maxLength: number | null): string {
	const content = String(value ?? '');
	if (!maxLength || maxLength < 1) return content;

	// Avoid truncating HTML fragments to prevent broken tags.
	if (/<\/?[a-z][\s\S]*>/i.test(content)) {
		return content;
	}

	return content.length > maxLength ? content.slice(0, maxLength) : content;
}

function resolveDataSourceMergeTags(
	template: string | undefined,
	dataSource?: Record<string, any>,
	maxLength?: number | null,
) {
	if (!template || !dataSource) return template || '';

	const getFormattedResolvedValue = (
		resolvedValue: unknown,
		resolvedFormat?: string,
		resolvedMaxLength?: number | null,
	): string | null => {
		const effectiveMaxLength =
			resolvedMaxLength === null || resolvedMaxLength === undefined
				? maxLength || null
				: resolvedMaxLength;

		if (
			typeof resolvedValue === 'string' ||
			typeof resolvedValue === 'number' ||
			typeof resolvedValue === 'boolean'
		) {
			return formatDynamicTextValue(resolvedValue, resolvedFormat, effectiveMaxLength);
		}

		if (Array.isArray(resolvedValue)) {
			const firstValue = resolvedValue.find(
				item =>
					typeof item === 'string' ||
					typeof item === 'number' ||
					typeof item === 'boolean',
			);
			if (firstValue !== undefined) {
				return formatDynamicTextValue(firstValue, resolvedFormat, effectiveMaxLength);
			}
		}

		return null;
	};

	const withConditionalsResolved = template.replace(
		IF_MERGE_TAG_PATTERN,
		(_, conditionKey, trueExpression, fallbackText) => {
			const fallback = String(fallbackText || '').trim();
			const parsedHelper = parseDynamicTextHelperExpression(
				String(trueExpression || '').trim(),
			);
			const actualKey = String(parsedHelper?.key || conditionKey || '').trim();
			if (!actualKey) return fallback;

			const resolvedValue = resolveMergeTagValue(dataSource, actualKey);
			const formattedValue = getFormattedResolvedValue(
				resolvedValue,
				parsedHelper?.format,
				parsedHelper?.maxLength,
			);

			if (formattedValue !== null && formattedValue.trim() !== '') {
				return formattedValue;
			}

			return fallback;
		},
	);

	return withConditionalsResolved.replace(MERGE_TAG_PATTERN, match => {
		const keyMatch = match.match(/\{\{\s*([^{}]+?)\s*\}\}/);
		const rawKey = String(keyMatch?.[1] || '');
		const trimmedRawKey = rawKey.trim();

		// Skip orphaned Handlebars block tokens
		if (
			trimmedRawKey.startsWith('#') ||
			trimmedRawKey.startsWith('/') ||
			trimmedRawKey === 'else'
		)
			return '';

		const parsedHelper = parseDynamicTextHelperExpression(trimmedRawKey);
		const pipeIdx = trimmedRawKey.indexOf('|');
		const actualKey = parsedHelper
			? parsedHelper.key
			: pipeIdx >= 0
				? trimmedRawKey.slice(0, pipeIdx).trim()
				: trimmedRawKey;
		const fallbackText =
			!parsedHelper && pipeIdx >= 0 ? trimmedRawKey.slice(pipeIdx + 1).trim() : '';

		const resolvedValue = resolveMergeTagValue(dataSource, actualKey);
		const formattedValue = getFormattedResolvedValue(
			resolvedValue,
			parsedHelper?.format,
			parsedHelper?.maxLength,
		);

		// A genuinely blank resolved value (a real field that exists but is empty)
		// is not a resolved value — fall through to the tag's own fallback text,
		// or the raw tag, same as an unresolved one. Never render nothing.
		if (formattedValue !== null && formattedValue.trim() !== '') {
			return formattedValue;
		}

		if (fallbackText) return fallbackText;
		return match;
	});
}

function escapeHtmlAttribute(value: string) {
	return String(value || '')
		.replace(/&/g, '&amp;')
		.replace(/"/g, '&quot;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;');
}

export type IButton = IBlockData<
	{
		align?: string;
		color?: string;
		'background-color'?: string;
		'container-background-color'?: string;
		border?: string;
		'border-radius'?: string;
		href?: string;
		rel?: string;
		target?: string;
		title?: string;
		padding?: string;
		'inner-padding'?: string;
		'text-align'?: string;
		'vertical-align'?: 'middle' | 'top' | 'bottom';
		width?: string;
		'font-family'?: string;
		'font-size'?: string;
		'font-style'?: string;
		'font-weight'?: string;
		'line-height'?: string;
		'letter-spacing'?: string;
		height?: string;
		'text-decoration'?: string;
		'text-transform'?: string;
		'white-space'?: string;
		'border-width'?: string;
		'border-style'?: string;
		'border-color'?: string;
	},
	{
		content: string;
		dynamicTextTag?: string;
		dynamicTextPlaceholder?: string;
		dynamicTextMaxLength?: number | string;
		dynamicTextId?: string;
		selectedListingId?: string;
		selectedListing?: AttachedListingData | null;
	}
>;

export const Button = createBlock<IButton>({
	get name() {
		return t('Button');
	},
	type: BasicType.BUTTON,
	create: payload => {
		const defaultData: IButton = {
			type: BasicType.BUTTON,
			data: {
				value: {
					content: 'Button',
				},
			},
			attributes: {
				align: 'center',
				'background-color': '#414141',
				color: '#ffffff',
				'font-weight': 'normal',
				'border-radius': '3px',
				padding: '0px 0px 0px 0px',
				'inner-padding': '10px 25px 10px 25px',
				'line-height': '120%',
				target: '_blank',
				'vertical-align': 'middle',
				border: 'none',
				'text-align': 'center',
				href: '',
				'container-background-color': '#FFFFFF',
				'font-family': 'Arial, sans-serif',
				'font-size': '14px',
				'letter-spacing': '0px',
				'text-decoration': 'none',
				'white-space': 'normal',
				'border-width': '0px',
				'border-style': 'solid',
				'border-color': '#000000',
				width: 'auto',
			},
			children: [],
		};
		return merge(defaultData, payload);
	},
	validParentType: [BasicType.COLUMN, BasicType.HERO],
	render(params) {
		const { data, mode } = params;
		const isPreviewMode = Boolean(params.dataSource?.previewMode);
		// An enclosing Listing Wrapper / listing column WINS — see the Text block.
		const selectedListing =
			getAncestorPinnedListing(params.context, params.idx) ||
			resolveBoundListing(data.data.value);
		const dynamicTextExpression = (data.data.value.dynamicTextTag || '').trim();
		const dynamicTextMaxLength = normalizeDynamicTextMaxLength(
			data.data.value.dynamicTextMaxLength,
		);
		const dynamicTextTag =
			dynamicTextExpression.match(/^\{\{\s*([^{}]+?)\s*\}\}$/)?.[1]?.trim() ||
			dynamicTextExpression;
		const dynamicTextPlaceholder = data.data.value.dynamicTextPlaceholder;
		const dynamicTextId = (data.data.value.dynamicTextId || '').trim();
		const dynamicMarkerId =
			dynamicTextId ||
			(dynamicTextTag && params.idx !== undefined && params.idx !== null
				? `btn_txt_${String(params.idx).replace(/[^a-zA-Z0-9_-]/g, '_')}`
				: '');

		// Combine separate border attributes into single border attribute
		const borderWidth = data.attributes['border-width'];
		const borderStyle = data.attributes['border-style'];
		const borderColor = resolvePreviewColor(
			data.attributes['border-color'],
			params.mode,
			params.dataSource,
		);

		// Create derived attributes (no mutation)
		let attributes = data.attributes;
		// See resolveViewerProfile — a link's user.* tag may answer from sample data,
		// same as text. Dynamic IMAGES deliberately do not (see the Image block).
		const realUserProfile = resolveViewerProfile(params.dataSource);
		const resolvedHref = resolveAttachedListingMergeTags((data.attributes.href as string) || '', {
			listing: selectedListing,
			userProfile: realUserProfile,
		});

		if (borderWidth || borderStyle || borderColor) {
			const combinedBorder = [
				borderWidth || '0px',
				borderStyle || 'solid',
				borderColor || '#000000',
			].join(' ');

			attributes = {
				...data.attributes,
				href: resolvedHref,
				border: combinedBorder,
			};
		} else {
			attributes = {
				...data.attributes,
				href: resolvedHref,
			};
		}

		const hasMergeTagPattern = (value: string) =>
			/\{\{\s*[^{}]+\s*\}\}/.test(String(value || ''));

		const defaultFallbackText = String(
			data.data.value.content || dynamicTextPlaceholder || '',
		);
		const fallbackSourceText = defaultFallbackText || dynamicTextExpression || '';

		// TODO(raster): not gated on dataSource.rasterMode the way the Text block
		// is, so a listing tag here still resolves to bare text in the raster copy
		// and loses its underline. Gate it the same way when this block's raster
		// output matters.
		let content = truncateDynamicTextValue(
			resolveAttachedListingMergeTags(fallbackSourceText, {
				listing: selectedListing,
				userProfile: realUserProfile,
			}),
			dynamicTextMaxLength,
		);

		if (mode === 'testing' && isPreviewMode && dynamicTextExpression) {
			const previewResolvedContent = resolveDataSourceMergeTags(
				dynamicTextExpression,
				getPreviewResolutionDataSource(params.dataSource, selectedListing),
				dynamicTextMaxLength,
			);
			const hasResolvedPreviewValue =
				!hasMergeTagPattern(previewResolvedContent) &&
				String(previewResolvedContent).trim().length > 0;

			if (hasResolvedPreviewValue || !content) {
				content = previewResolvedContent;
			}
		}

		if (mode === 'production' && dynamicTextTag && dynamicMarkerId) {
			content = `<span id="${dynamicMarkerId}" data-dynamic-tag="${escapeHtmlAttribute(dynamicTextTag)}">${content}</span>`;
		}

		content = content.replace(
			/^(?:<(div|span)\s+style=["']\s*white-space\s*:\s*(?:normal|nowrap)\s*;?\s*["']>([\s\S]*)<\/\1>)+$/i,
			(_, __, inner) => inner,
		);
		if (attributes['white-space'] === 'nowrap') {
			content = `<span style="white-space:nowrap;">${content}</span>`;
		}
		return (
			<BasicBlock
				params={{
					...params,
					data: {
						...data,
						attributes,
					},
				}}
				tag='mj-button'
			>
				{content}
			</BasicBlock>
		);
	},
});
